import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'

/**
 * Admin Dashboard - Lead Management System
 * 
 * Features:
 * - Secure admin login
 * - View all leads
 * - Lead statistics
 * - Update lead status
 * - Delete leads
 * - Export functionality
 */

interface Lead {
  id: string
  timestamp: string
  name: string
  company: string
  brief: string
  budgetBand: string
  preferredCall?: string
  selectedPackage?: string
  status: 'new' | 'contacted' | 'converted'
  emailSent: boolean
  emailError?: string
}

interface Stats {
  total: number
  new: number
  contacted: number
  converted: number
  emailSent: number
  emailFailed: number
  thisWeek: number
}

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [leads, setLeads] = useState<Lead[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(false)
  const [loginForm, setLoginForm] = useState({ username: '', password: '' })
  const [error, setError] = useState('')

  // Check if user is already logged in
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token')
    if (savedToken) {
      setToken(savedToken)
      setIsAuthenticated(true)
      fetchLeads()
      fetchStats()
    }
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginForm)
      })

      const data = await response.json()

      if (data.success) {
        setToken(data.token)
        setIsAuthenticated(true)
        localStorage.setItem('admin_token', data.token)
        fetchLeads()
        fetchStats()
      } else {
        setError(data.error || 'Login failed')
      }
    } catch (error) {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    setToken(null)
    setIsAuthenticated(false)
    localStorage.removeItem('admin_token')
    setLeads([])
    setStats(null)
  }

  const fetchLeads = async () => {
    try {
      const response = await fetch('/api/admin/leads', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      const data = await response.json()
      if (data.success) {
        setLeads(data.leads)
      }
    } catch (error) {
      console.error('Failed to fetch leads:', error)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      const data = await response.json()
      if (data.success) {
        setStats(data.stats)
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error)
    }
  }

  const updateLeadStatus = async (leadId: string, status: string) => {
    try {
      const response = await fetch(`/api/admin/leads/${leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      })

      if (response.ok) {
        fetchLeads()
        fetchStats()
      }
    } catch (error) {
      console.error('Failed to update lead:', error)
    }
  }

  const deleteLead = async (leadId: string) => {
    if (!confirm('Are you sure you want to delete this lead?')) return

    try {
      const response = await fetch(`/api/admin/leads/${leadId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        fetchLeads()
        fetchStats()
      }
    } catch (error) {
      console.error('Failed to delete lead:', error)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-500'
      case 'contacted': return 'bg-yellow-500'
      case 'converted': return 'bg-green-500'
      default: return 'bg-gray-500'
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-luxury-black flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-luxury-gray/50 backdrop-blur-sm rounded-2xl p-8 w-full max-w-md border border-luxury-gold/20"
        >
          <h1 className="text-2xl font-bold text-luxury-gold text-center mb-6">
            Admin Login
          </h1>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Username
              </label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                className="w-full px-4 py-2 bg-luxury-black border border-luxury-gold/30 rounded-lg text-white focus:border-luxury-gold focus:outline-none"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full px-4 py-2 bg-luxury-black border border-luxury-gold/30 rounded-lg text-white focus:border-luxury-gold focus:outline-none"
                required
              />
            </div>
            
            {error && (
              <div className="text-red-400 text-sm text-center">
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-luxury-gold text-luxury-black py-2 px-4 rounded-lg font-medium hover:bg-luxury-gold/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-luxury-black p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-luxury-gold">
            MindGlow Admin Dashboard
          </h1>
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Logout
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-luxury-gray/50 backdrop-blur-sm rounded-lg p-4 border border-luxury-gold/20"
            >
              <div className="text-2xl font-bold text-luxury-gold">{stats.total}</div>
              <div className="text-sm text-gray-400">Total Leads</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-luxury-gray/50 backdrop-blur-sm rounded-lg p-4 border border-luxury-gold/20"
            >
              <div className="text-2xl font-bold text-blue-400">{stats.new}</div>
              <div className="text-sm text-gray-400">New</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-luxury-gray/50 backdrop-blur-sm rounded-lg p-4 border border-luxury-gold/20"
            >
              <div className="text-2xl font-bold text-yellow-400">{stats.contacted}</div>
              <div className="text-sm text-gray-400">Contacted</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-luxury-gray/50 backdrop-blur-sm rounded-lg p-4 border border-luxury-gold/20"
            >
              <div className="text-2xl font-bold text-green-400">{stats.converted}</div>
              <div className="text-sm text-gray-400">Converted</div>
            </motion.div>
          </div>
        )}

        {/* Leads Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-luxury-gray/50 backdrop-blur-sm rounded-lg border border-luxury-gold/20 overflow-hidden"
        >
          <div className="p-6 border-b border-luxury-gold/20">
            <h2 className="text-xl font-semibold text-luxury-gold">
              All Leads ({leads.length})
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-luxury-black/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-luxury-gold">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-luxury-gold">Company</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-luxury-gold">Package</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-luxury-gold">Budget</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-luxury-gold">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-luxury-gold">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-luxury-gold">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-luxury-gold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-luxury-gold/10">
                {leads.map((lead, index) => (
                  <motion.tr
                    key={lead.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-luxury-black/30"
                  >
                    <td className="px-4 py-3 text-white">{lead.name}</td>
                    <td className="px-4 py-3 text-gray-300">{lead.company}</td>
                    <td className="px-4 py-3 text-gray-300">{lead.selectedPackage || 'General'}</td>
                    <td className="px-4 py-3 text-gray-300">${lead.budgetBand}</td>
                    <td className="px-4 py-3">
                      <select
                        value={lead.status}
                        onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                        className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(lead.status)} text-white`}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="converted">Converted</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${
                        lead.emailSent ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                      }`}>
                        {lead.emailSent ? 'Sent' : 'Failed'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-sm">
                      {formatDate(lead.timestamp)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => deleteLead(lead.id)}
                        className="text-red-400 hover:text-red-300 text-sm"
                      >
                        Delete
                      </button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
